import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import Input from 'ui/Input';
import Phone from 'ui/Phone';
import Modal from 'ui/Modal';
import sendForm from './utils/sendForm';
import { messageValidator, nameValidator, numberValidator, emailValidator } from 'utils/validator';
import { getSearchParameters } from 'utils/helpers/functions';
import styles from 'sections/Company/ContactUs/components/Contact/Form/Form.module.scss';

const cn = classNames.bind(styles);

interface IForm {
  isAuth: boolean;
  contact: any;
  page?: 'erp' | null;
}

type QueryMessages = {
  [key: string]: string;
};

enum EForm {
  name = 'name',
  number = 'number',
  email = 'email',
  message = 'message',
}

const QueryMessages: QueryMessages = {
  interested_in_summit_2024: 'Hello, I am interested in participating in Global Freight Summit 2024.',
};

const Form: FC<IForm> = ({ isAuth, contact, page }): React.ReactElement => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [loaderForm, setLoaderForm] = useState<boolean>(false);
  const [mask, setMask] = useState<string>('');
  const [lockedText, setLockedText] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({
    name: '',
    number: {},
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState<any>({
    name: '',
    number: '',
    email: '',
    message: '',
  });

  useEffect(() => {
    const query = getSearchParameters();
    if (query && query.event) {
      if (!!QueryMessages[query.event]) {
        const message = QueryMessages[query.event];
        setLockedText(message);
        setFormData((state: any) => ({ ...state, message }));
        return;
      }

      if (query.event !== 'dfa_premium') return;
      const message = 'Request more information about DFA Premium';
      setLockedText(message);
      setFormData((state: any) => ({ ...state, message }));
    }
  }, []);

  const validator = (): boolean => {
    const nameValidatorBool: boolean = nameValidator({ formData, setErrors });
    const emailValidatorBool: boolean = emailValidator({ formData, setErrors });
    const messageValidatorBool: boolean = messageValidator({ formData, setErrors });
    const numberValidatorBool: boolean = numberValidator({ formData, setErrors, mask, isAuth: !!contact.name });

    return nameValidatorBool && emailValidatorBool && messageValidatorBool && numberValidatorBool;
  };

  const submitHandler = async (e: any) => {
    e.preventDefault();
    if (!validator()) return;
    setLoaderForm(true);

    await sendForm({ formData, platform: contact.platform });

    setModalVisible(true);
    setLoaderForm(false);
  };

  useEffect(() => {
    isAuth &&
      setFormData((state: any) => ({
        ...state,
        name: contact.name,
        email: contact.email,
        number: { iso: '', phone: contact.phone },
      }));
  }, [isAuth]);

  const modalHandler = () => (location.href = location.origin);

  return (
    <form
      className={cn('form')}
      onSubmit={submitHandler}
    >
      <Modal
        title="Your message has been successfully sent."
        text="We will contact you very soon!"
        btnText="Main page"
        modalHandler={modalHandler}
        open={modalVisible}
        setModalVisible={setModalVisible}
        contact={contact}
      />

      <header className={cn('form__header')}>
        {page === 'erp' ? (
          <>
            <h1 className={cn('form__heading')} style={{ marginLeft: 0 }}>
              Become a partner
            </h1>
          </>
        ) : (
          <>
            <img
              className={cn('form__icon')}
              src="/images/pages/Company/ContactUs/mail.svg"
              alt="Contact mail"
            />
            <h1 className={cn('form__heading')}>Contact Us</h1>
          </>
        )}
      </header>

      <div className={cn('form__body')}>
        <Input
          label="Name"
          name={EForm.name}
          placeholder="Darlene Robertson"
          error={errors.name}
          value={isAuth ? contact.name : ''}
          setFormData={setFormData}
          setErrors={setErrors}
        />

        <Phone
          label="PHONE number"
          error={errors.number}
          name="number"
          value={isAuth ? contact.phone : ''}
          setFormData={setFormData}
          setErrors={setErrors}
          callback={(mask) => setMask(mask)}
        />

        <Input
          label="E-mail"
          type="email"
          name={EForm.email}
          placeholder="darlenerobertson@mail.com"
          value={isAuth ? contact.email : ''}
          error={errors.email}
          setFormData={setFormData}
          setErrors={setErrors}
        />
      </div>

      <Input
        label="Message"
        type="textarea"
        name={EForm.message}
        placeholder="Your message..."
        error={errors.message}
        setFormData={setFormData}
        setErrors={setErrors}
        lockedText={lockedText}
        style={{ marginTop: 32 }}
      />

      <div className={cn('form__buttons')}>
        <button className={cn('form__button-submit')} type='submit'>
          {loaderForm ? <div className={cn('form__loader')} /> : ''}
          Send message
        </button>
      </div>
    </form>
  );
};

export { Form };
